'use client';
import { STATUS, SURFACE, BORDER } from '@/constants/colors';
import { memo, useState, useCallback } from 'react';
import {
  Box,
  Chip,
  TextField,
  InputAdornment,
  Stack,
  IconButton,
  Drawer,
  Typography,
  Button,
  Divider,
  Badge,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Search,
  FilterList,
  Close,
  Sort,
  Check,
  TuneRounded,
  KeyboardArrowDown,
} from '@mui/icons-material';
import { themeColors } from '../theme/theme';
import { useTranslation } from '@/kit/useTranslation';

/**
 * SmartFilter - A compact, space-efficient filter component
 * Features:
 * - Collapsible filter drawer (mobile-first)
 * - Inline quick filters for most common options
 * - Search always visible
 * - Active filter count badge
 * - Sort options in drawer
 */
export const SmartFilter = memo(
  ({
    options = [],
    value,
    onChange,
    onSearch,
    searchPlaceholder: searchPlaceholderProp,
    sortOptions = [],
    onSortChange,
    currentSort,
    quickFilterCount = 3, // Number of quick filters to show inline
    showSearch = true,
  }) => {
    const { t } = useTranslation();
    const searchPlaceholder = searchPlaceholderProp || t('common.searchPlaceholder');
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const handleSearchChange = useCallback(
      (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        if (onSearch) onSearch(query);
      },
      [onSearch]
    );

    const handleFilterChange = useCallback(
      (newValue) => {
        onChange(newValue);
        if (!isDesktop) {
          setDrawerOpen(false);
        }
      },
      [onChange, isDesktop]
    );

    // Get quick filters (first N options + ALL)
    const quickFilters = options.slice(0, quickFilterCount);
    const moreFilters = options.slice(quickFilterCount);

    // Count active non-default filters
    const activeFilterCount = value && value !== 'ALL' ? 1 : 0;
    const activeSortCount = currentSort && currentSort !== 'time' ? 1 : 0;
    const totalActiveCount = activeFilterCount + activeSortCount;

    const getOptionValue = (opt) => (typeof opt === 'string' ? opt : opt.value);
    const getOptionLabel = (opt) => (typeof opt === 'string' ? opt : opt.label);

    return (
      <Box sx={{ mb: 2 }}>
        {/* Main Search & Filter Bar */}
        <Stack direction="row" spacing={1} alignItems="center">
          {/* Search */}
          {showSearch && (
            <TextField
              size="small"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={searchPlaceholder}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#9ca3af', fontSize: 20 }} />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 2.5,
                  bgcolor: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  height: 40,
                  '& fieldset': { border: 'none' },
                  '&:hover': { borderColor: '#d1d5db' },
                  '&.Mui-focused': {
                    borderColor: themeColors.primaryColor,
                    boxShadow: `0 0 0 3px ${alpha(themeColors.primaryColor, 0.1)}`,
                  },
                },
              }}
              sx={{ flex: 1 }}
            />
          )}

          {/* Quick Filters (inline on desktop) */}
          {isDesktop && (
            <Stack direction="row" spacing={0.75}>
              {quickFilters.map((opt) => {
                const optValue = getOptionValue(opt);
                const optLabel = getOptionLabel(opt);
                const isActive = value === optValue;
                return (
                  <Chip
                    key={optValue}
                    label={optLabel}
                    size="small"
                    onClick={() => handleFilterChange(optValue)}
                    sx={{
                      height: 32,
                      borderRadius: 2,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      bgcolor: isActive ? themeColors.primaryColor : '#f3f4f6',
                      color: isActive ? '#fff' : '#4b5563',
                      border: 'none',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: isActive ? themeColors.primaryColor : '#e5e7eb',
                      },
                    }}
                  />
                );
              })}
            </Stack>
          )}

          {/* Filter Button */}
          <IconButton
            onClick={() => setDrawerOpen(true)}
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2.5,
              bgcolor: totalActiveCount > 0 ? alpha(themeColors.primaryColor, 0.1) : '#f3f4f6',
              color: totalActiveCount > 0 ? themeColors.primaryColor : '#6b7280',
              border: '1px solid',
              borderColor: totalActiveCount > 0 ? alpha(themeColors.primaryColor, 0.3) : '#e5e7eb',
              '&:hover': {
                bgcolor: totalActiveCount > 0 ? alpha(themeColors.primaryColor, 0.15) : '#e5e7eb',
              },
            }}
          >
            <Badge
              badgeContent={totalActiveCount}
              color="primary"
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.65rem',
                  height: 16,
                  minWidth: 16,
                  bgcolor: themeColors.primaryColor,
                },
              }}
            >
              <TuneRounded sx={{ fontSize: 20 }} />
            </Badge>
          </IconButton>
        </Stack>

        {/* Quick filter chips for mobile (most common only) */}
        {!isDesktop && (
          <Stack
            direction="row"
            spacing={0.75}
            sx={{
              mt: 1.5,
              overflowX: 'auto',
              pb: 0.5,
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
            }}
          >
            {quickFilters.map((opt) => {
              const optValue = getOptionValue(opt);
              const optLabel = getOptionLabel(opt);
              const isActive = value === optValue;
              return (
                <Chip
                  key={optValue}
                  label={optLabel}
                  size="small"
                  onClick={() => handleFilterChange(optValue)}
                  sx={{
                    height: 28,
                    borderRadius: 1.5,
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    flexShrink: 0,
                    bgcolor: isActive ? themeColors.primaryColor : '#fff',
                    color: isActive ? '#fff' : '#4b5563',
                    border: '1px solid',
                    borderColor: isActive ? themeColors.primaryColor : '#e5e7eb',
                    '&:hover': {
                      bgcolor: isActive ? themeColors.primaryColor : '#f9fafb',
                    },
                  }}
                />
              );
            })}
            {moreFilters.length > 0 && (
              <Chip
                label={t('common.nMore', { count: moreFilters.length })}
                size="small"
                onClick={() => setDrawerOpen(true)}
                sx={{
                  height: 28,
                  borderRadius: 1.5,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  flexShrink: 0,
                  bgcolor: '#fff',
                  color: '#6b7280',
                  border: '1px dashed #d1d5db',
                  '&:hover': { bgcolor: '#f9fafb' },
                }}
              />
            )}
          </Stack>
        )}

        {/* Filter Drawer */}
        <Drawer
          anchor="bottom"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              maxHeight: '70vh',
              pb: 'env(safe-area-inset-bottom)',
            },
          }}
        >
          <Box sx={{ p: 2.5 }}>
            {/* Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
              <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#1f2937' }}>
                {t('common.filtersAndSort')}
              </Typography>
              <IconButton onClick={() => setDrawerOpen(false)} size="small">
                <Close sx={{ fontSize: 20 }} />
              </IconButton>
            </Stack>

            {/* Status Filters */}
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 700,
                color: '#6b7280',
                mb: 1.5,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {t('common.status')}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1} mb={3}>
              {options.map((opt) => {
                const optValue = getOptionValue(opt);
                const optLabel = getOptionLabel(opt);
                const isActive = value === optValue;
                return (
                  <Chip
                    key={optValue}
                    label={optLabel}
                    size="small"
                    icon={isActive ? <Check sx={{ fontSize: 14 }} /> : undefined}
                    onClick={() => handleFilterChange(optValue)}
                    sx={{
                      height: 32,
                      borderRadius: 2,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      bgcolor: isActive ? themeColors.primaryColor : '#f3f4f6',
                      color: isActive ? '#fff' : '#4b5563',
                      border: 'none',
                      '& .MuiChip-icon': { color: '#fff', ml: 0.5 },
                      '&:hover': {
                        bgcolor: isActive ? themeColors.primaryColor : '#e5e7eb',
                      },
                    }}
                  />
                );
              })}
            </Stack>

            {/* Sort Options */}
            {sortOptions.length > 0 && (
              <>
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: '#6b7280',
                    mb: 1.5,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {t('common.sortBy')}
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1} mb={3}>
                  {sortOptions.map((opt) => {
                    const isActive = currentSort === opt.value;
                    return (
                      <Chip
                        key={opt.value}
                        label={opt.label}
                        size="small"
                        icon={
                          isActive ? (
                            <Check sx={{ fontSize: 14 }} />
                          ) : (
                            <Sort sx={{ fontSize: 14 }} />
                          )
                        }
                        onClick={() => onSortChange?.(opt.value)}
                        variant={isActive ? 'filled' : 'outlined'}
                        sx={{
                          height: 32,
                          borderRadius: 2,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          bgcolor: isActive ? alpha(themeColors.primaryColor, 0.1) : 'transparent',
                          color: isActive ? themeColors.primaryColor : '#6b7280',
                          borderColor: isActive ? themeColors.primaryColor : '#d1d5db',
                          '& .MuiChip-icon': {
                            color: isActive ? themeColors.primaryColor : '#9ca3af',
                          },
                          '&:hover': {
                            bgcolor: alpha(themeColors.primaryColor, 0.08),
                            borderColor: themeColors.primaryColor,
                          },
                        }}
                      />
                    );
                  })}
                </Stack>
              </>
            )}

            {/* Apply Button */}
            <Button
              fullWidth
              variant="contained"
              onClick={() => setDrawerOpen(false)}
              sx={{
                py: 1.5,
                borderRadius: 2.5,
                fontWeight: 700,
                fontSize: '0.9rem',
                textTransform: 'none',
                bgcolor: '#0f172a',
                '&:hover': { bgcolor: '#1e293b' },
              }}
            >
              {t('common.applyFilters')}
            </Button>

            {/* Reset */}
            {(activeFilterCount > 0 || activeSortCount > 0) && (
              <Button
                fullWidth
                variant="text"
                onClick={() => {
                  onChange('ALL');
                  onSortChange?.('time');
                }}
                sx={{
                  mt: 1,
                  py: 1,
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  textTransform: 'none',
                  color: '#6b7280',
                }}
              >
                {t('common.clearAllFilters')}
              </Button>
            )}
          </Box>
        </Drawer>
      </Box>
    );
  }
);

SmartFilter.displayName = 'SmartFilter';

export default SmartFilter;
